import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemConfirm,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO, TeamBillingPortalDTO } from "@shaarp/common";
import { ChevronDownIcon, TriangleAlertIcon } from "lucide-react";
import { useState } from "react";
import { DialogAdminTeamBilling } from "./dialog-admin-team-billing";
import { DialogAdminTeamBillingSyncConfirm } from "./dialog-admin-team-billing-sync-confirm";
import { DataTableCellAdminsUnpaidInvoice } from "./data-table-cell-admins-unpaid-invoice";

export default function DataTableCellAdminsBilling({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const { t, currentLanguage } = useTranslation();
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { refreshTeams } = useAdminTeamsEmitter();

  const [openBillingModal, setOpenBillingModal] = useState(false);

  const doRemoveBillingError = async () => {
    try {
      await api.delete(`/admin/team/${team.id}/billing/error`);
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.billingErrorRemoved"),
        description: t(
          "admin.dataTableAdminTeams.toast.billingErrorRemovedDescription",
        ),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    }
  };

  const doCancelBilling = async () => {
    try {
      await api.post(`/admin/team/${team.id}/billing/cancel`);
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.billingCancelled"),
        description: t(
          "admin.dataTableAdminTeams.toast.billingCancelledDescription",
        ),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    }
  };

  const doResumeBilling = async () => {
    try {
      await api.post(`/admin/team/${team.id}/billing/resume`);
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.billingResumed"),
        description: t(
          "admin.dataTableAdminTeams.toast.billingResumedDescription",
        ),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    }
  };

  const doOpenStripePage = async () => {
    window.open(
      `https://dashboard.stripe.com/customers/${team.stripeCustomerId}`,
      "_blank",
    );
  };

  const doOpenStripePortal = async () => {
    try {
      const { data } = await api.get<TeamBillingPortalDTO>(
        `/admin/team/${team.id}/billing/portal`,
      );

      if (data && data.url) {
        window.open(data.url, "_blank")?.focus();
      }
    } catch (e) {
      toastError();
    }
  };

  const handleBillingSync = async (
    toggleBilling: boolean,
    push: boolean,
    pull: boolean,
  ) => {
    try {
      await api.post(`/admin/team/${team.id}/billing/sync`, {
        toggleBilling,
        push,
        pull,
      });
      refreshTeams();
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.billingMenu.successTitle"),
        description: t(
          "admin.dataTableAdminTeams.billingMenu.successDescription",
        ),
      });
    } catch (e) {
      toastError();
    }
  };

  return (
    <div className="flex gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Badge
            variant={team.billingEnabled ? "success" : "outline"}
            className="cursor-pointer"
          >
            {team.billingEnabled
              ? t("admin.dataTableAdminTeams.badges.activated")
              : t("admin.dataTableAdminTeams.badges.deactivated")}
            <ChevronDownIcon className="ml-2 w-3 h-3" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DialogAdminTeamBillingSyncConfirm
            teamId={team.id}
            toggleBilling={true}
            push={true}
            pull={false}
            title={
              !team.billingEnabled
                ? t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.toggleEnableTitle",
                  )
                : t(
                    "admin.dataTableAdminTeams.billingMenu.syncPreview.toggleDisableTitle",
                  )
            }
            onConfirm={() => handleBillingSync(true, true, false)}
          >
            {!team.billingEnabled
              ? t("admin.dataTableAdminTeams.billingMenu.activateAndSync")
              : t("admin.dataTableAdminTeams.billingMenu.deactivateAndSync")}
          </DialogAdminTeamBillingSyncConfirm>
          <DropdownMenuItemConfirm
            text={
              team.billingEnabled
                ? t("admin.dataTableAdminTeams.billingMenu.disconnect")
                : t("admin.dataTableAdminTeams.billingMenu.reconnect")
            }
            description={t(
              "admin.dataTableAdminTeams.billingMenu.disconnectDescription",
            )}
            btnConfirmText={t("admin.dataTableAdminTeams.billingMenu.continue")}
            onConfirm={() => handleBillingSync(true, false, false)}
          >
            {team.billingEnabled
              ? t("admin.dataTableAdminTeams.billingMenu.disconnect")
              : t("admin.dataTableAdminTeams.billingMenu.reconnect")}
          </DropdownMenuItemConfirm>
          <DialogAdminTeamBillingSyncConfirm
            teamId={team.id}
            push={false}
            pull={true}
            onConfirm={() => handleBillingSync(false, false, true)}
          >
            {t("admin.dataTableAdminTeams.billingMenu.pullSync")}
          </DialogAdminTeamBillingSyncConfirm>
          <DialogAdminTeamBillingSyncConfirm
            teamId={team.id}
            push={true}
            pull={false}
            onConfirm={() => handleBillingSync(false, true, false)}
          >
            {t("admin.dataTableAdminTeams.billingMenu.pushSync")}
          </DialogAdminTeamBillingSyncConfirm>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => doOpenStripePage()}>
            {t("admin.dataTableAdminTeams.actions.openStripeSheet")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => doOpenStripePortal()}>
            {t("admin.dataTableAdminTeams.actions.openStripePortal")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpenBillingModal(true)}>
            {t("admin.dataTableAdminTeams.actions.modifyBilling")}
          </DropdownMenuItem>
          {team.billingEnabled === true &&
            team.stripeCustomerId &&
            !team.stripeMonthlySubscriptionEndsAt &&
            !team.stripeAnnualSubscriptionEndsAt && (
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminTeams.actions.confirmCancelBilling",
                )}
                description={t(
                  "admin.dataTableAdminTeams.actions.cancelBillingDescription",
                  {
                    date:
                      new Date(
                        team.stripeMonthlySubscriptionCurrentPeriodEndsAt || "",
                      ).toLocaleDateString(currentLanguage) || t("common.errorUnknown"),
                  },
                )}
                btnConfirmText={t(
                  "admin.dataTableAdminTeams.actions.cancelBillingButton",
                )}
                onConfirm={doCancelBilling}
                className="text-destructive"
              >
                {t("admin.dataTableAdminTeams.actions.cancelBilling")}
              </DropdownMenuItemConfirm>
            )}
          {team.billingEnabled === true &&
            team.stripeCustomerId &&
            (team.stripeMonthlySubscriptionEndsAt ||
              team.stripeAnnualSubscriptionEndsAt) && (
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminTeams.actions.confirmResumeBilling",
                )}
                description={t(
                  "admin.dataTableAdminTeams.actions.resumeBillingDescription",
                )}
                btnConfirmText={t(
                  "admin.dataTableAdminTeams.actions.resumeBillingButton",
                )}
                onConfirm={doResumeBilling}
              >
                {t("admin.dataTableAdminTeams.actions.resumeBilling")}
              </DropdownMenuItemConfirm>
            )}
        </DropdownMenuContent>
      </DropdownMenu>

      {openBillingModal && (
        <DialogAdminTeamBilling
          team={team}
          open={openBillingModal}
          onClose={() => setOpenBillingModal(false)}
        />
      )}

      {team.billingError && (
        <Tooltip delayDuration={0}>
          <TooltipTrigger>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Badge variant="destructive" className="cursor-pointer">
                  <TriangleAlertIcon className="w-3 h-3" />
                  <ChevronDownIcon className="ml-1 w-2 h-2" />
                </Badge>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItemConfirm
                  text={t(
                    "admin.dataTableAdminTeams.actions.confirmRemoveBillingError",
                  )}
                  onConfirm={doRemoveBillingError}
                  btnConfirmText={t(
                    "admin.dataTableAdminTeams.actions.removeBillingErrorButton",
                  )}
                  description={t(
                    "admin.dataTableAdminTeams.actions.removeBillingErrorDescription",
                  )}
                  className="text-destructive"
                >
                  {t("admin.dataTableAdminTeams.actions.removeBillingError")}
                </DropdownMenuItemConfirm>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent>
            {t("admin.dataTableAdminTeams.tooltips.lastBillingError", {
              error: team.billingError || t("common.errorUnknown"),
            })}
          </TooltipContent>
        </Tooltip>
      )}

      {team.unpaidInvoice && (
        <DataTableCellAdminsUnpaidInvoice invoice={team.unpaidInvoice} />
      )}
    </div>
  );
}
